import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { alpha, Theme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import React from 'react';

import { PageContainer } from '../../components/atoms/PageContainer';
import { LABEL_COLUMN_WIDTH } from '../../components/organisms/ServiceActivityMatrix/ServiceActivityMatrix.styles';
import { ACTIVITY_LANES, ACTIVITY_STATUS_COLUMNS } from '../../constants/service-activity.constant';

/** Skeletons are decorative; the wrapper announces the loading state once. */
const SKELETON_SX = {
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

/** Enough rows to fill the fold without implying a specific result count. */
const PLACEHOLDER_ROWS = 12;
const GROUP_ROW_EVERY = 4;

const onDark = (opacity: number) => (theme: Theme) => alpha(theme.palette.common.white, opacity);

const HeaderBand: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      // Wraps and grows exactly as HomeActivityHeader does, so a narrow window
      // does not shift the layout when the real header replaces this one.
      flexWrap: 'wrap',
      bgcolor: 'v4.sidebar',
      px: '20px',
      py: '12px',
      minHeight: 45,
    }}
  >
    <Stack direction="row" alignItems="center" gap={1.25}>
      <Skeleton
        variant="text"
        width={104}
        height={16}
        sx={{ ...SKELETON_SX, bgcolor: onDark(0.22) }}
      />
      <Skeleton
        variant="text"
        width={78}
        height={12}
        sx={{ ...SKELETON_SX, bgcolor: onDark(0.13) }}
      />
    </Stack>
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Skeleton
        variant="text"
        width={186}
        height={12}
        sx={{ ...SKELETON_SX, bgcolor: onDark(0.13) }}
      />
      <Skeleton
        variant="rounded"
        width={34}
        height={18}
        sx={{ ...SKELETON_SX, bgcolor: onDark(0.13) }}
      />
    </Stack>
  </Box>
);

/** Mirrors ServiceFlowRail: three lanes, divided, each with its real stage count. */
const FlowRailSkeleton: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      gap: '10px',
      px: '6px',
      py: '8px',
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'v4.border',
      borderRadius: 2,
    }}
  >
    {ACTIVITY_LANES.map((lane, laneIndex) => {
      const stages = ACTIVITY_STATUS_COLUMNS.filter((column) => column.lane === lane.lane);

      return (
        <React.Fragment key={lane.lane}>
          {laneIndex > 0 && (
            <Box sx={{ width: '1px', bgcolor: 'v4.border', flexShrink: 0, my: '2px' }} />
          )}
          <Box
            sx={{
              flex: stages.length,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '8px' }}>
              <Skeleton variant="text" width={54} height={10} sx={SKELETON_SX} />
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'v4.border' }} />
              <Skeleton variant="text" width={22} height={10} sx={SKELETON_SX} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              {stages.map((stage) => (
                <Box
                  key={stage.field}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    px: '8px',
                    py: '6px',
                  }}
                >
                  <Skeleton variant="text" width="72%" height={10} sx={SKELETON_SX} />
                  <Skeleton variant="text" width="54%" height={22} sx={SKELETON_SX} />
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={4}
                    sx={SKELETON_SX}
                    data-testid="skeleton-flow-stage-bar"
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </React.Fragment>
      );
    })}
  </Box>
);

const MatrixRowSkeleton: React.FC<{ isGroup: boolean }> = ({ isGroup }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      height: isGroup ? 30 : 32,
      bgcolor: isGroup ? 'v4.tableEven' : 'transparent',
      borderBottom: 1,
      borderColor: 'v4.border',
    }}
  >
    <Box sx={{ width: LABEL_COLUMN_WIDTH, flexShrink: 0, px: '10px' }}>
      <Skeleton
        variant="text"
        width={isGroup ? 62 : 96}
        height={12}
        sx={{ ...SKELETON_SX, ml: isGroup ? 0 : '20px' }}
      />
    </Box>
    {ACTIVITY_STATUS_COLUMNS.map((column) => (
      <Box key={column.field} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <Skeleton variant="text" width={20} height={12} sx={SKELETON_SX} />
      </Box>
    ))}
    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
      <Skeleton variant="text" width={26} height={12} sx={SKELETON_SX} />
    </Box>
  </Box>
);

/** Mirrors the matrix panel: title, toolbar, sticky header row, grouped rows.
 *  Built from Boxes rather than a real <table> so the loading state never
 *  matches the `Service Activity Monitor` table role the e2e smoke test waits
 *  on — it should resolve on real data, not on the placeholder. */
const MatrixSkeleton: React.FC = () => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'v4.border',
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '14px',
        py: '10px',
        borderBottom: 1,
        borderColor: 'v4.border',
      }}
    >
      <Skeleton variant="text" width={98} height={16} sx={SKELETON_SX} />
      <Stack direction="row" alignItems="center" gap={1}>
        <Skeleton variant="text" width={44} height={12} sx={SKELETON_SX} />
        <Skeleton variant="rounded" width={180} height={26} sx={SKELETON_SX} />
        <Skeleton variant="rounded" width={86} height={26} sx={SKELETON_SX} />
      </Stack>
    </Box>

    {/* Same width contract as the live matrix: 900px minimum, scrolling
        sideways rather than clipping, so a narrow window shows the same shape
        before and after the data arrives. */}
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 900 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 40,
            bgcolor: 'v4.tableEven',
            borderBottom: 1,
            borderColor: 'v4.borderDark',
          }}
        >
          <Box sx={{ width: LABEL_COLUMN_WIDTH, flexShrink: 0, px: '10px' }}>
            <Skeleton variant="text" width={112} height={10} sx={SKELETON_SX} />
          </Box>
          {[...ACTIVITY_STATUS_COLUMNS, null].map((column, index) => (
            <Box
              key={column?.field ?? 'total'}
              sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
            >
              <Skeleton
                variant="text"
                width={index % 2 === 0 ? 44 : 34}
                height={10}
                sx={SKELETON_SX}
              />
            </Box>
          ))}
        </Box>

        {Array.from({ length: PLACEHOLDER_ROWS }, (_, index) => (
          <MatrixRowSkeleton key={index} isGroup={index % GROUP_ROW_EVERY === 0} />
        ))}
      </Box>
    </Box>
  </Box>
);

/** Loading state for the home page, shaped like the page it precedes so the
 *  layout does not jump when the data lands. */
export const HomeSkeleton: React.FC = () => (
  <Box role="status" aria-live="polite">
    <Box sx={visuallyHidden}>Loading service activity</Box>
    {/* Header outside PageContainer, mirroring Home so nothing shifts. */}
    <HeaderBand />
    <PageContainer>
      <Stack gap={1}>
        <FlowRailSkeleton />
        <MatrixSkeleton />
      </Stack>
    </PageContainer>
  </Box>
);
